import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';

import {
  useServiceProfileActualizar,
  useServiceProfileCrear,
  useServiceProfileDesactivar,
  useServiceProfiles,
  type IServiceProfileCatalogo,
} from '@/shared/actions/erp/tickets-soporte-cliente/softnet-import.actions';

/**
 * Administración del catálogo de service profiles.
 *
 * Es la fuente EXTRA de datos para aprovisionar: no reemplaza el modelo que
 * resuelve el paso 3 (inventario del ERP o Excel del área técnica), sino que
 * cubre lo que ese no alcanza. El micro de aprovisionamiento valida el modelo
 * contra su propio inventario y rechaza la activación si no lo conoce, y es ahí
 * donde se quedaban clientes sin servicio.
 *
 * Orden con el que se intenta activar a cada cliente:
 *   1. el modelo que resolvió el sistema,
 *   2. el de este catálogo para ese mismo modelo, cuando está escrito distinto,
 *   3. el comodín, para los modelos que no están.
 */

/** Fila en edición: null = nada abierto, sin id = fila nueva. */
interface Edicion {
  id_service_profile?: string;
  nombre: string;
  id_perfil: string;
  observacion: string;
}

const VACIA: Edicion = { nombre: '', id_perfil: '', observacion: '' };

interface Props {
  abierto: boolean;
  onCerrar: () => void;
  /** Sin permiso de ejecución la tabla se ve pero no se edita. */
  puedeEditar?: boolean;
}

const ServiceProfilesDialog: React.FC<Props> = ({ abierto, onCerrar, puedeEditar = false }) => {
  const { data, isFetching, refetch } = useServiceProfiles({ enabled: abierto });
  const crear = useServiceProfileCrear();
  const actualizar = useServiceProfileActualizar();
  const desactivar = useServiceProfileDesactivar();

  const [edicion, setEdicion] = useState<Edicion | null>(null);
  const [error, setError] = useState<string | null>(null);

  const filas = (data as { data?: { resultados?: IServiceProfileCatalogo[] } } | undefined)
    ?.data?.resultados ?? [];
  const guardando = crear.isPending || actualizar.isPending || desactivar.isPending;

  const guardar = async () => {
    if (!edicion) return;
    setError(null);
    const nombre = edicion.nombre.trim();
    const idPerfil = Number(edicion.id_perfil);
    if (!nombre) {
      setError('El nombre del modelo es obligatorio: es lo que viaja al microservicio.');
      return;
    }
    if (!Number.isInteger(idPerfil)) {
      setError('El ID del perfil debe ser un número entero.');
      return;
    }
    try {
      if (edicion.id_service_profile) {
        await actualizar.mutateAsync({
          id_service_profile: edicion.id_service_profile,
          nombre,
          id_perfil: idPerfil,
          observacion: edicion.observacion.trim(),
        });
      } else {
        await crear.mutateAsync({
          nombre,
          id_perfil: idPerfil,
          observacion: edicion.observacion.trim(),
        });
      }
      setEdicion(null);
      refetch();
    } catch {
      // El hook ya mostró el detalle del backend (nombre repetido, etc.).
    }
  };

  const alternarActivo = async (f: IServiceProfileCatalogo) => {
    setError(null);
    try {
      if (f.activo) {
        await desactivar.mutateAsync(f.id_service_profile);
      } else {
        await actualizar.mutateAsync({ id_service_profile: f.id_service_profile, activo: true });
      }
      refetch();
    } catch {
      // Igual: el hook ya reportó.
    }
  };

  return (
    <Dialog open={abierto} onClose={onCerrar} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Service profiles del aprovisionamiento
          </Typography>
          <Tooltip arrow title="Recargar">
            <IconButton onClick={() => refetch()} disabled={isFetching}>
              {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Tooltip>
          <IconButton onClick={onCerrar}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Alta / edición */}
        {puedeEditar && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            {edicion ? (
              <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap" useFlexGap>
                <TextField
                  size="small"
                  label="Modelo (service_profile)"
                  value={edicion.nombre}
                  onChange={(e) => setEdicion({ ...edicion, nombre: e.target.value })}
                  helperText="Tal como lo espera el microservicio"
                  sx={{ minWidth: 220 }}
                />
                <TextField
                  size="small"
                  label="ID del perfil"
                  value={edicion.id_perfil}
                  onChange={(e) => setEdicion({ ...edicion, id_perfil: e.target.value })}
                  helperText="El de la OLT; no se envía, es para auditar"
                  sx={{ width: 180 }}
                />
                <TextField
                  size="small"
                  label="Observación"
                  value={edicion.observacion}
                  onChange={(e) => setEdicion({ ...edicion, observacion: e.target.value })}
                  sx={{ minWidth: 240, flexGrow: 1 }}
                />
                <Button
                  variant="contained"
                  disableElevation
                  startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                  disabled={guardando}
                  onClick={guardar}
                  sx={{ textTransform: 'none', minHeight: 40, mt: 0.25 }}
                >
                  Guardar
                </Button>
                <Button
                  onClick={() => {
                    setEdicion(null);
                    setError(null);
                  }}
                  sx={{ textTransform: 'none', minHeight: 40, mt: 0.25 }}
                >
                  Cancelar
                </Button>
              </Stack>
            ) : (
              <Button
                startIcon={<AddIcon />}
                onClick={() => setEdicion({ ...VACIA })}
                sx={{ textTransform: 'none', minHeight: 40 }}
              >
                Agregar un modelo
              </Button>
            )}
          </Paper>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Modelo (service_profile)</TableCell>
                <TableCell align="right">ID perfil</TableCell>
                <TableCell>Observación</TableCell>
                <TableCell align="center">Estado</TableCell>
                {puedeEditar && <TableCell align="right">Acciones</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filas.length === 0 && (
                <TableRow>
                  <TableCell colSpan={puedeEditar ? 5 : 4}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }} align="center">
                      {isFetching ? 'Cargando...' : 'El catálogo está vacío.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {filas.map((f) => (
                <TableRow key={f.id_service_profile} hover sx={{ opacity: f.activo ? 1 : 0.55 }}>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {f.nombre}
                      </Typography>
                      {f.es_generico && (
                        <Tooltip
                          arrow
                          title="Comodín: se usa cuando el modelo del equipo no está en el catálogo. No se puede desactivar."
                        >
                          <Chip size="small" color="warning" label="comodín" />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{f.id_perfil}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {f.observacion ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      variant="outlined"
                      color={f.activo ? 'success' : 'default'}
                      label={f.activo ? 'activo' : 'inactivo'}
                    />
                  </TableCell>
                  {puedeEditar && (
                    <TableCell align="right">
                      <Tooltip arrow title="Editar">
                        <IconButton
                          size="small"
                          disabled={guardando}
                          onClick={() =>
                            setEdicion({
                              id_service_profile: f.id_service_profile,
                              nombre: f.nombre,
                              id_perfil: String(f.id_perfil),
                              observacion: f.observacion ?? '',
                            })
                          }
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip
                        arrow
                        title={
                          f.es_generico
                            ? 'El comodín no se puede desactivar'
                            : f.activo
                              ? 'Desactivar (la fila se conserva para auditar)'
                              : 'Reactivar'
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={guardando || f.es_generico}
                            onClick={() => alternarActivo(f)}
                          >
                            {f.activo ? <BlockIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            La baja es lógica: las migraciones ya hechas guardan con qué service profile activaron,
            y el ID del perfil es lo único que permite auditar con qué perfil quedó un equipo.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCerrar} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServiceProfilesDialog;
